import { EFinancingProgram } from '@if/core/types'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '@src/services/api-client'
import { CreditApplicationMessage } from '../../types/CreditApplicationMessage'
import { buildUrlWithIds } from '../make-api'

const MESSAGE_BASE_URL = '/Applications/{financingProgramId}/{creditApplicationId}/Messages'

const SCOPE = 'message'
const LIST = 'list'

// Query Keys Factory
const keysFactory = {
  all: [SCOPE] as const,
  list: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    [SCOPE, LIST, creditApplicationId, financingProgramId] as const,
}

// GET OPERATIONS

const getMessageList = async (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(MESSAGE_BASE_URL, { creditApplicationId, financingProgramId })
  const response = await apiClient.get<CreditApplicationMessage[]>(url)
  return response.data
}

export function useGetMessageList(
  creditApplicationId: string | undefined,
  financingProgramId: EFinancingProgram | undefined,
): [CreditApplicationMessage[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.list(creditApplicationId ?? '', financingProgramId ?? ('' as EFinancingProgram)),
    queryFn: () => getMessageList(creditApplicationId!, financingProgramId!),
    enabled: !!creditApplicationId && !!financingProgramId,
  })

  return [data ?? [], isFetching]
}

// CREATE OPERATION

type CreateMessageDto = Partial<CreditApplicationMessage>

const createMessage: MutationFunction<CreditApplicationMessage, CreateMessageDto> = async (data) => {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(MESSAGE_BASE_URL, {
    creditApplicationId: data.creditApplicationId!,
    financingProgramId: data.financingProgramId!,
  })
  const response = await apiClient.post<CreditApplicationMessage>(url, data)
  return response.data
}

export function useCreateMessage(): [MutationFunction<CreditApplicationMessage, CreateMessageDto>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: createMessage,
    onSuccess: (createdMessage, params) => {
      const queryKey = keysFactory.list(params.creditApplicationId!, params.financingProgramId!)
      const currentData = queryClient.getQueryData<CreditApplicationMessage[]>(queryKey)
      if (currentData) {
        queryClient.setQueryData<CreditApplicationMessage[]>(queryKey, [...currentData, createdMessage])
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// SEND REQUIRED DOCUMENTS MESSAGE OPERATION

interface SendRequiredDocumentsMessageDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

const sendRequiredDocumentsMessage: MutationFunction<CreditApplicationMessage, SendRequiredDocumentsMessageDto> = async (data) => {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(MESSAGE_BASE_URL.concat('/SendRequiredDocumentsMessage'), {
    creditApplicationId: data.creditApplicationId,
    financingProgramId: data.financingProgramId,
  })
  const response = await apiClient.post<CreditApplicationMessage>(url)
  return response.data
}

export function useSendRequiredDocumentsMessage(): [
  MutationFunction<CreditApplicationMessage, SendRequiredDocumentsMessageDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: sendRequiredDocumentsMessage,
    onSuccess: (createdMessage, params) => {
      const queryKey = keysFactory.list(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<CreditApplicationMessage[]>(queryKey)
      if (currentData) {
        queryClient.setQueryData<CreditApplicationMessage[]>(queryKey, [...currentData, createdMessage])
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// DELETE OPERATION

interface DeleteMessageDto {
  id: string
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

const deleteMessage: MutationFunction<void, DeleteMessageDto> = async (data) => {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(MESSAGE_BASE_URL.concat('/{id}'), {
    creditApplicationId: data.creditApplicationId,
    financingProgramId: data.financingProgramId,
    id: data.id,
  })
  await apiClient.delete(url)
}

export function useDeleteMessage(): [MutationFunction<void, DeleteMessageDto>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: deleteMessage,
    onSuccess: (_result, params) => {
      const queryKey = keysFactory.list(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<CreditApplicationMessage[]>(queryKey)
      if (currentData) {
        queryClient.setQueryData<CreditApplicationMessage[]>(queryKey, currentData.filter((m) => m.id !== params.id))
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
